import {
  load,
  showLog,
  showError
} from '../../utils';
import {
  DEFAULT_PREBID_LOAD_TIMEOUT,
  PREBID_URL,
  SHOW_LOGS
} from '../../constants';

const REASONS = {
  ERROR_LOAD_LIBRARY:         'ERROR_LOAD_LIBRARY',
  TIMEOUT_REACHED_GLOBAL:     'TIMEOUT_REACHED_GLOBAL',
  TIMEOUT_REACHED_FETCH_BIDS: 'TIMEOUT_REACHED_FETCH_BIDS',
};
const {RSVP} = window;
const NAME = 'PREBID';

// NOTE: This is the global variable that Prebid searches for
window.pbjs                         = window.pbjs || {};
window.pbjs.que                     = window.pbjs.que || [];
window.pbjs.adserverRequestResponse = false;

export default Object.preventExtensions({
  NAME: function() {return NAME;},
  isActive({headerBidding}) {
    if (headerBidding) {
      showLog('Prebid::Is active');
    } else {
      showLog('Prebid::Is NOT active');
    }
    return headerBidding;
  },
  /**
   * Launch prebid behaviour
   * @param  {object}  adUnits - adUnits config
   * @param  {object}  biddersConfig
   * @param  {integer} timeout
   * @return {Promise}
   */
  getLoadPromise({headerBidding}) {
    return new RSVP.Promise(function(resolve, reject) {
      var timeout = headerBidding.timeout || DEFAULT_PREBID_LOAD_TIMEOUT;
      _loadLibrary()
        .then(function() {
          showLog('Prebid::getLoadPromise::Library loaded');
          _setConfig(headerBidding);
          return _fetchBid(headerBidding);
        })
        .then(function() {
          resolve(true);
        });

      setTimeout(function() {
        reject(REASONS.TIMEOUT_REACHED_GLOBAL);
      }, timeout);
    }).catch(function(reason) {
      switch (reason) {
        case REASONS.TIMEOUT_REACHED_GLOBAL:
          showLog('Prebid::getLoadPromise::TIMEOUT reached global');
          break;
        case REASONS.TIMEOUT_REACHED_FETCH_BIDS:
          showLog('Prebid::getLoadPromise::TIMEOUT reached in _fetchBid');
          break;
        case REASONS.ERROR_LOAD_LIBRARY:
          showError('Prebid::_loadLibrary::Error loading library', reason);
          break;
        default:
          showError('Prebid::getLoadPromise::Error in Prebid process', reason);
      }
      return false;
    });
  },

  /**
   * Force setTargeting
   */
  setTargetings() {
    if (window.pbjs.adserverRequestResponse) {
      window.googletag.cmd.push(function() {
        window.pbjs.que.push(function() {
          showLog('Prebid::setTargetings::Get targetings', window.pbjs.getAdserverTargeting());
          window.pbjs.setTargetingForGPTAsync();
          showLog('Prebid::setTargetings::Targetings added');
        });
      });
    } else {
      showLog('Prebid::setTargetings::Targetings NOT added because no response from bidders');
    }
  }
});

/* Private */

/**
 * Load prebid library
 * @return {Promise}
 */
function _loadLibrary() {
  showLog('Prebid::_loadLibrary::Loading library');
  return load(PREBID_URL)
    .catch(function(reason) {
      showError('Prebid::_loadLibrary', reason);
      return RSVP.reject(REASONS.ERROR_LOAD_LIBRARY);
    });
}

function _setConfig(headerBidding) {
  var biddersConfig, pbjsConfig;

  biddersConfig = headerBidding.biddersConfig;
  // NOTE: set custom config object
  pbjsConfig = {
    debug            : SHOW_LOGS,
    enableSendAllBids: headerBidding.enableSendAllBids || true,
    priceGranularity : {
      buckets : [{
        precision: 2,  // NOTE: default is 2 if omitted - means 2.123456 rounded to 4 decimal places
        min:       0,
        max:       20,
        increment: 0.01
      }]
    }
  };

  showLog('Prebid::_setConfig::biddersConfig', biddersConfig);
  window.pbjs.bidderSettings = _fetchBiddersSettings(biddersConfig);
  window.pbjs.setConfig(pbjsConfig);
  showLog('Prebid::_setConfig::Show Prebid config', window.pbjs.getConfig());
}

/**
 * Add adUnits config and make prebid call
 * @param {object}  config    - headerBidding config from configGDPMedio
 */
function _fetchBid(headerBidding) {
  var adUnits, timeout;

  adUnits = headerBidding.adUnits;
  timeout = headerBidding.timeout || DEFAULT_PREBID_LOAD_TIMEOUT;

  return new RSVP.Promise(function(resolve, reject) {
    window.pbjs.que.push(function() {
      showLog('Prebid::_fetchBid::Add adUnits', adUnits);
      window.pbjs.addAdUnits(adUnits);
      showLog('Prebid::_fetchBid::Request Prebid');
      window.pbjs.requestBids({
        timeout,
        bidsBackHandler: function(biddersResponse) {
          showLog('Prebid::_fetchBid::Response from Prebid', biddersResponse);
          if (Object.keys(biddersResponse).length === 0) {
            reject(REASONS.TIMEOUT_REACHED_FETCH_BIDS);
            return;
          }
          window.pbjs.adserverRequestResponse = true;
          resolve();
        }
      });
    });
  });
}

/**
 * Build bidders settings from config
 * @param  {object} biddersConfig - biddersConfig from configGDPMedio.headerBidding
 * @return {object} bidderSettings
 */
function _fetchBiddersSettings(biddersConfig) {
  const bidderSettings = {};

  Object.keys(biddersConfig).forEach(function(bidderName) {
    bidderSettings[bidderName] = {
      bidCpmAdjustment : function(bidCpm, bid) {
        var percent;

        percent = (1 - ('percent' in biddersConfig[bidderName] ? biddersConfig[bidderName].percent / 100 : 0));
        // NOTE: adjust the bid in real time before the auction takes place
        showLog('Prebid::_fetchBiddersSettings::bidCpmAdjustment Bidder is:', bid.bidderCode, 'with percent:', percent);
        // NOTE: return bidCpm * .85; Ex.: rest 15% to the gross price
        return bidCpm * percent;
      }
    };
  });

  return bidderSettings;
}



// WEBPACK FOOTER //
// ./src/header-bidding/providers/prebid.js